/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import "./themes/theme1/theme1-v1.css";
import "./themes/theme1/responsive-theme1-v1.css";
import { Box, Text } from "@chakra-ui/react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import CartSidebarPage from "./theme1/components/CartSidebar";
import CategoriesBar from "./theme1/components/CategoriesBar";
import StoreHeader from "./theme1/components/Header";
import HeroSection from "./theme1/components/HeroSection";
import OrdersModal from "./theme1/components/OrdersModal";
import ProductCard from "./theme1/components/ProductCard";
import ProductModal from "./theme1/components/ProductModal";
import ProductList from "./theme1/components/ProductsList";
import ReviewModal from "./theme1/components/ReviewModal";
import OrderDetailsPage from "./theme1/components/OrderDetailsPage";
import EditOrderModal from "./theme1/components/EditOrderModal";
import {
  getItems,
  getCategories,
  getCoupons,
  getTenantDetails,
  getBanners,
  getTags,
  getCities,
  getStoreReviews,
  addStoreReview,
  getCustomerOrders,
  normalizeTenantDetails,
  getCurrentOrder, getCurrentCustomer,
  updateTenantOrder,
  cancelCustomerOrder,
  getDeliveryByCity
} from './utils/storeApi';
import { useDisplayData, filterProductsByDivision } from "./theme1/hooks/useDisplayData";
import { useSmartCart } from './hooks/useSmartCart';
import AuthModal from './components/AuthModal';
import { submitStoreCheckout, showStoreAlert } from "./utils/submitStoreCheckout";
import Swal from "sweetalert2";
import "sweetalert2/src/sweetalert2.scss";
import { buildImageUrl, n, safeText } from "./theme1/utils/theme1Helpers";
import { THEMES, THEME_NAME_TO_ID, ORDER_STATUS, menuBtnStyle } from "./theme1/utils/theme1Constants";
import { useStoreBootstrap } from "./theme1/hooks/useStoreBootstrap"

export default function Theme1StorePage({ tenantId, tenant }) {


  const [pendingCartItem, setPendingCartItem] = useState(null);

  const [selectedCityId, setSelectedCityId] = useState("");

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, description: "" });
  const [customerOrders, setCustomerOrders] = useState([]);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [sortValue, setSortValue] = useState("");
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [modalVariant, setModalVariant] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    loading, error,
    storeData, apiProducts, apiCategories, apiCoupons, apiBanners, apiTags, storeReviews,
    setStoreReviews, cities
  } = useStoreBootstrap(tenantId);
  const {
    displayProducts,
    displayCategories,
    displayCoupons,
    displayTags
  } = useDisplayData({
    apiProducts,
    apiCategories,
    apiCoupons,
    apiTags
  });

  const allVariants = useMemo(() => {
    if (!apiProducts?.length) return [];
    const all = [];

    apiProducts.forEach(p => {
      (p.variants || []).forEach(v => {
        all.push({
          itemId: p.id,
          itemName: p.name || "",
          description: p.description || "",
          discountValue: p.discountValue || 0,
          discountType: p.discountType || 0,
          colorHex: v.color?.hex || v.colorHex || "",
          variantId: Number(v.variantId),
          basePrice: Number(v.basePrice),
          finalPrice: Number(v.finalPrice),
          size: v.size || (v.options?.[0]?.label) || "",
          imageUrl: v.imageUrl || p.mainImage || p.mainImageUrl,
          stockQty: Number(v.stockQty || 0)
        });
      });
    });
    return all;
  }, [apiProducts]);
  // Use a ref to always hold the up-to-date all products list
  const allowThemeChange = false;

  // ========== Customer Management ==========
  const [customer, setCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem("storeCustomer");
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const [showAuthModal, setShowAuthModal] = useState(false);


  // On mount, restore customer state from localStorage for robustness
  useEffect(() => {
    try {
      const saved = localStorage.getItem("storeCustomer");
      if (saved) setCustomer(JSON.parse(saved));
    } catch { }
  }, []);

  // Persist customer to localStorage whenever it changes
  useEffect(() => {
    if (customer) {
      try {
        localStorage.setItem("storeCustomer", JSON.stringify(customer));
      } catch { }
    } else {
      try {
        localStorage.removeItem("storeCustomer");
        localStorage.removeItem("storeCustomerId");
      } catch { }
    }
  }, [customer]);
  // ========== Fetch All Store Data ==========



  // ========== Smart Cart Hook ==========
  const {
    cart,
    addItem: addItem,
    updateQuantity: updateQuantity,
    removeItem: removeItem,
    clearCart: clearCart,
    refreshCart: refreshCart,
    isLoggedIn: isLoggedIn,
  } = useSmartCart(tenantId);

  useEffect(() => {
    if (displayProducts?.length) {
      window.__STORE_PRODUCTS__ = displayProducts;
      // إعادة تحميل السلة بعد اكتمال جلب المنتجات لضمان تعيين ألوان Hex الصحيحة
      refreshCart();
    }
  }, [displayProducts, refreshCart]);

  // داخل useMemo الخاص بـ subtotal في Theme1StorePage.js
  const subtotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      // item.price الآن هو الصافي دائماً
      const price = Number(item.price || 0);
      const qty = Number(item.quantity || 0);
      return sum + (price * qty);
    }, 0);
  }, [cart]);

  // ========== UI States ==========
  const [selectedTheme, setSelectedTheme] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCartSidebar, setShowCartSidebar] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingOrder, setEditingOrder] = useState(null);

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1400);
  const [deliveryForm, setDeliveryForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    cityId: localStorage.getItem("selectedCityId") || ""
  });
  useEffect(() => {
    const savedCustomer = JSON.parse(localStorage.getItem("storeCustomer"));
    if (savedCustomer) {
      setDeliveryForm(prev => ({
        ...prev,
        name: savedCustomer.name || "",
        email: savedCustomer.email || "",
        phone: savedCustomer.phone || "",
        address: savedCustomer.address || "",
        cityId: savedCustomer.cityId || prev.cityId || "", // Prioritize profile, fallback to previous choice
      }));
    }
  }, []);

  // Sync deliveryForm with customer profile whenever customer state changes (login/logout)
  useEffect(() => {
    if (customer) {
      setDeliveryForm({
        name: customer.name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        address: customer.address || "",
        cityId: customer.cityId || deliveryForm.cityId || "",
      });
    }
  }, [customer]);

  // Persist city selection even if not logged in
  useEffect(() => {
    if (deliveryForm.cityId) {
      localStorage.setItem("selectedCityId", deliveryForm.cityId);
    }
  }, [deliveryForm.cityId]);

  useEffect(() => {
    if (deliveryForm.cityId && tenantId) {
      getDeliveryByCity(tenantId, deliveryForm.cityId).then(res => {
        if (res) setDeliveryInfo(res);
        else setDeliveryInfo(null);
      });
    } else {
      setDeliveryInfo(null);
    }
  }, [deliveryForm.cityId, tenantId]);

  const resolvedThemeId = useMemo(() => {
    const normalizeName = (val) => (typeof val === "string" ? val.trim() : "");
    const storeColor = normalizeName(storeData.colorName);
    const tenantColor = normalizeName(tenant?.colorName);

    if (storeColor && THEME_NAME_TO_ID[storeColor]) {
      return THEME_NAME_TO_ID[storeColor];
    }
    if (storeData.themeId) return Number(storeData.themeId);

    if (tenantColor && THEME_NAME_TO_ID[tenantColor]) {
      return THEME_NAME_TO_ID[tenantColor];
    }
    if (tenant?.themeId) return Number(tenant.themeId);
    return 1;
  }, [storeData.themeId, storeData.colorName, tenant?.themeId, tenant?.colorName]);

  const theme = THEMES[resolvedThemeId] || THEMES[1];
  const asText = (value) => (value === null || value === undefined ? "" : String(value).trim());
  const asNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };


  const saveEditedOrder = async (order) => {

    if (!order.details || order.details.length === 0) {
      Swal.fire("خطأ", "الطلب فارغ", "error");
      return;
    }

    const city = cities.find(c => Number(c.cityId) === Number(order.cityId));

    if (!city) {
      Swal.fire("خطأ", "المحافظة غير صحيحة", "error");
      return;
    }

    const normalizedOrder = {
      orderId: order.orderId,
      orderNo: asNumber(order.orderNo, 0),
      tenantId: asNumber(order.tenantId, 0),
      tenantID: asNumber(order.tenantId, 0),

      statusId: asNumber(order.statusId, 1),
      status: asText(order.status) || "new",

      totalAmount: asNumber(order.totalAmount, 0),
      discountAmount: asNumber(order.discountAmount, 0),
      netAmount: asNumber(order.netAmount, 0),

      deliveryId: 0,
      customerId: asNumber(order.customerId, 0),
      customerName: asText(order.customerName),
      cityId: asNumber(order.cityId, 0),
      city: asText(order.city),
      address: asText(order.address),
      customerPhone1: asText(order.customerPhone1),

      paymentMethodName: asText(order.paymentMethodName) || "Cash",
      orderDate: asText(order.orderDate) || new Date().toISOString(),
      transactionId: asText(order.transactionId),
      coupon: asText(order.coupon),
      paymentStatus: asText(order.paymentStatus) || "new",
      isPaid: asNumber(order.isPaid, 0),

      // 🔥 هذا هو المهم
      items: order.details.map(d => ({
        itemId: asNumber(d.ItemId || d.itemId, 0),
        variantId: asNumber(d.VariantId || d.variantId, 0),
        itemName: asText(d.ItemName || d.itemName),
        variantName: asText(d.VariantName || d.variantName),
        colorName: asText(d.ColorName || d.colorName),
        size: asText(d.Size || d.size),
        quantity: asNumber(d.Quantity || d.quantity, 0),
        price: asNumber(d.Price || d.price, 0),
        imageUrl: asText(d.ImageUrl || d.imageUrl),
        stockQty: asNumber(d.StockQty || d.stockQty || 0, 0),
        discount: 0,
        lineTotal: asNumber(d.Quantity || d.quantity, 0) * asNumber(d.Price || d.price, 0)
      }))
    };

    const res = await updateTenantOrder(tenantId, normalizedOrder, city);

    if (res.success) {
      const savedOrder = res.data?.order || res.data?.data || res.data || normalizedOrder;
      setCustomerOrders(prev =>
        prev.map(o =>
          o.orderId === order.orderId
            ? { ...o, ...savedOrder, status: "Pending" }
            : o
        )
      );

      setEditingOrder(null);
      Swal.fire("تم", "تم حفظ التعديلات بنجاح", "success");
    } else {
      Swal.fire("خطأ", res.error || "فشل حفظ التعديلات", "error");
    }
  };





  const handleCheckoutSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isSubmitting) return;

    if (!localStorage.getItem("storeCustomerId")) {
      setShowAuthModal(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitStoreCheckout({
        tenantId,
        cart,
        cities,
        deliveryForm,
        deliveryInfo,
        subtotal,
        total,
        couponDiscount,
      });

      if (result.needAuth) {
        setShowAuthModal(true);
        return;
      }

      if (!result.ok) {
        await showStoreAlert({
          icon: "error",
          title: result.title || "خطأ",
          text: result.error,
        });
        return;
      }

      localStorage.setItem("lastOrderId", result.orderId || "");
      localStorage.removeItem("storeCurrentOrder");
      localStorage.removeItem("storeCoupon");
      setAppliedCoupon(null);
      setCouponError("");
      clearCart();
      await refreshCart();

      await showStoreAlert({
        icon: "success",
        title: "تم إنشاء الطلب بنجاح",
        text: "سيتم التواصل معك لتأكيد الطلب",
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const lastOrderId = localStorage.getItem("lastOrderId");












  const policyCardStyle = (theme) => ({
    background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
    borderRadius: "15px",
    padding: "20px",
    color: "white",
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: `0 4px 15px ${theme.primary}66`,
  });
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (Array.isArray(displayProducts) && displayProducts.length) {
      setProducts(displayProducts);
    }
  }, [displayProducts]);

  // Replace with single state:
  const visibleProducts = useMemo(() => {
    let data = filterProductsByDivision(products, selectedCategory, selectedSubCategory);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(p =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q)
      );
    }

    if (sortValue === "hightolow") {
      data.sort((a, b) => {
        const priceA = n((a.selectedVariant?.basePrice || 0) - (a.selectedVariant?.discount || 0));
        const priceB = n((b.selectedVariant?.basePrice || 0) - (b.selectedVariant?.discount || 0));
        return priceB - priceA;
      });
    } else if (sortValue === "lowtohigh") {
      data.sort((a, b) => {
        const priceA = n((a.selectedVariant?.basePrice || 0) - (a.selectedVariant?.discount || 0));
        const priceB = n((b.selectedVariant?.basePrice || 0) - (b.selectedVariant?.discount || 0));
        return priceA - priceB;
      });
    } else if (sortValue === "alphabetical") {
      data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortValue === "big_discount") {
      data.sort((a, b) => {
        const discountA = n(a.selectedVariant?.discount || 0);
        const discountB = n(b.selectedVariant?.discount || 0);
        return discountB - discountA;
      });
    } else if (sortValue === "small_discount") {
      // Small discount but > 0
      data.sort((a, b) => {
        const discountA = n(a.selectedVariant?.discount || 0);
        const discountB = n(b.selectedVariant?.discount || 0);
        if (discountA === 0) return 1;
        if (discountB === 0) return -1;
        return discountA - discountB;
      });
    }

    return data;
  }, [products, selectedCategory, selectedSubCategory, searchQuery, sortValue]);







  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Cart state is now local and managed here only
  useEffect(() => {
    if (products?.length) {
      window.__STORE_PRODUCTS__ = products;
    }
  }, [products]);


  const couponDiscount = useMemo(() => {
    if (!appliedCoupon) return 0;

    const v = n(appliedCoupon.discountValue);
    const type = Number(appliedCoupon.discountType || 2); // 2 = %

    if (type === 1) {
      // مبلغ ثابت
      return Math.min(v, subtotal);
    }

    // نسبة مئوية
    const pct = Math.min(Math.max(v, 0), 100);
    return (subtotal * pct) / 100;
  }, [appliedCoupon, subtotal]);


  const shipping = useMemo(() => {
    if (!deliveryInfo) return 0;
    return Number(deliveryInfo.deliveryCompanyFees || 0);
  }, [deliveryInfo]);

  const total = subtotal - couponDiscount + shipping;

  const handleCategoryClick = (categoryId, subCategoryId = null) => {
    setSelectedCategory(Number(categoryId));
    setSelectedSubCategory(subCategoryId ? Number(subCategoryId) : null);
  };



  // Sort products by price
  const handleSortChange = (e) => setSortValue(e.target.value);

  const handleOpenProduct = ({ productId, variantId }) => {
    const p = products.find(x => x.id === productId);
    if (!p) return;

    const v = p.variants.find(x => Number(x.variantId) === Number(variantId));
    if (!v) return;

    setSelectedProduct({
      productId,
      variantId: v.variantId
    });

    setShowProductModal(true);
  };











  const handleAddToCart = async (data, qtyToAdd = 1) => {
    if (!localStorage.getItem("storeCustomerId")) {
      setPendingCartItem({ ...data, quantity: qtyToAdd });
      setShowAuthModal(true);
      return;
    }

    const res = await addItem(data, qtyToAdd);

    if (res?.success) {
      refreshCart();
    } else {
      Swal.fire("تنبيه", res?.error || "فشل إضافة المنتج", "warning");
    }
  };

  const handleUpdateQty = async (variantId, delta) => {
    const res = await updateQuantity(variantId, delta);
    if (!res.success && res.error) {
      Swal.fire("تنبيه", res.error, "warning");
    } else {
      await refreshCart();
    }
  };




  const handleRemove = async (variantId) => {
    const res = await removeItem(variantId);
    refreshCart();
    return res;
  };


  const handleLogout = () => {
    localStorage.removeItem("storeCustomer");
    localStorage.removeItem("storeCustomerId");
    localStorage.removeItem("storeCustomerToken");
    setDeliveryForm({
      name: "",
      email: "",
      phone: "",
      address: "",
      cityId: localStorage.getItem("selectedCityId") || ""
    });
    Swal.fire({
      icon: 'success',
      title: 'تم تسجيل الخروج بنجاح',
      text: 'تم تسجيل الخروج بنجاح',
    });
    setCustomer(null);
    clearCart();
    refreshCart();
  };

  const loadCustomerOrders = async () => {
    const customer = getCurrentCustomer();
    if (!customer?.customerId) return;

    const res = await getCustomerOrders(tenantId, customer.customerId);
    if (res.success) setCustomerOrders(res.data);
  };
  const handleLoginSuccess = (loginResponse) => {

    const fullCustomerData = {
      ...loginResponse,
      cityId: loginResponse.cityId || null,
      cityName: loginResponse.city || ""
    };

    localStorage.setItem("storeCustomerId", loginResponse.customerId);
    localStorage.setItem("storeCustomer", JSON.stringify(fullCustomerData));

    setCustomer(fullCustomerData);
    setShowLoginModal(false);
    refreshCart();
  };


  const canEditOrDelete = (order) =>
    Number(order.statusId) === ORDER_STATUS.PENDING;
  const handleDeleteOrder = async (order) => {
    const confirm = await Swal.fire({
      title: "تأكيد الحذف",
      text: "هل تريد حذف هذا الطلب نهائياً؟",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "نعم احذف",
      cancelButtonText: "إلغاء"
    });

    if (!confirm.isConfirmed) return;

    const res = await cancelCustomerOrder(tenantId, order.orderId);

    if (res.success) {
      setCustomerOrders(prev => prev.filter(o => o.orderId !== order.orderId));
      setSelectedOrderDetails(null);

      Swal.fire("تم", "تم حذف الطلب بنجاح", "success");
    } else {
      Swal.fire("خطأ", res.error || "فشل حذف الطلب", "error");
    }
  };




  useEffect(() => {
    if (showOrdersModal) loadCustomerOrders();
  }, [showOrdersModal]);





  // Handle coupon apply
  const applyCouponCode = (rawCode) => {
    setCouponError("");

    if (cart.length === 0) {
      setCouponError("لا يمكن استخدام الكوبون والسلة فارغة");
      return false;
    }

    const code = (rawCode || couponCode).toUpperCase().trim();
    if (!code) return false;

    const coupon = displayCoupons.find(c => c.code === code);
    if (!coupon) {
      setCouponError("الكوبون غير صحيح أو غير فعال");
      return false;
    }

    setAppliedCoupon(coupon);
    setCouponCode("");

    // ✅ خزّن الكوبون ليُرسل مع الطلب
    localStorage.setItem("storeCoupon", JSON.stringify(coupon));
    return true;
  };

  const handleApplyCoupon = (e) => {
    e.preventDefault();
    applyCouponCode();
  };



  // Handle search



  // Disable body scroll when modal is open
  useEffect(() => {
    if (showProductModal || showLoginModal || showAuthModal) {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = windowWidth > 768 ? "0px" : "0px";
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [showProductModal, showLoginModal, showAuthModal, windowWidth]);

  // Custom scrollbar styling for cart and product modal
  useEffect(() => {
    const styleId = 'custom-scrollbar-style';
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    const scrollbarStyles = `
      .cart-scrollable::-webkit-scrollbar,
      .product-modal-scrollable::-webkit-scrollbar {
        width: 8px;
      }
      .cart-scrollable::-webkit-scrollbar-track,
      .product-modal-scrollable::-webkit-scrollbar-track {
        background: ${theme.background};
        border-radius: 10px;
      }
      .cart-scrollable::-webkit-scrollbar-thumb,
      .product-modal-scrollable::-webkit-scrollbar-thumb {
        background: ${theme.primary};
        border-radius: 10px;
        border: 2px solid ${theme.background};
      }
      .cart-scrollable::-webkit-scrollbar-thumb:hover,
      .product-modal-scrollable::-webkit-scrollbar-thumb:hover {
        background: ${theme.primaryDark};
      }
      /* Firefox */
      .cart-scrollable,
      .product-modal-scrollable {
        scrollbar-width: thin;
        scrollbar-color: ${theme.primary} ${theme.background};
      }
    `;

    styleElement.textContent = scrollbarStyles;

    return () => {
      const element = document.getElementById(styleId);
      if (element) {
        element.remove();
      }
    };
  }, [theme.primary, theme.primaryDark, theme.background]);

  // Responsive styles
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 992;

  // ========== Loading & Error States ==========
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: theme.background
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <h2 style={{ color: theme.primary }}>جاري تحميل المتجر...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: theme.background
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
          <h2 style={{ color: '#E74C3C' }}>{error}</h2>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              background: theme.primary,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }







  return (
    <div
      className="theme-one theme1-v1 rtl-store"
      dir="rtl"
      style={{
        fontFamily: "'Inter', sans-serif",
        backgroundColor: theme.background,
        direction: "rtl",
        textAlign: "right"
      }}
    >

      {showReviewModal && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 20000
        }}>
          <div style={{
            background: "white",
            padding: "25px",
            borderRadius: "12px",
            width: "100%",
            maxWidth: "400px"
          }}>
            <h3>قيم تجربتك معنا</h3>

            <select
              value={newReview.rating}
              onChange={(e) => setNewReview({ ...newReview, rating: e.target.value })}
            >
              {[5, 4, 3, 2, 1].map(v => (
                <option key={v} value={v}>{v} نجوم</option>
              ))}
            </select>

            <textarea
              placeholder="اكتب رأيك هنا"
              value={newReview.description}
              onChange={(e) => setNewReview({ ...newReview, description: e.target.value })}
              style={{ width: "100%", marginTop: "10px" }}
            />

            <button
              onClick={async () => {
                const customer = JSON.parse(localStorage.getItem("storeCustomer"));

                await addStoreReview({
                  tenantId,
                  customerName: customer?.customerName || customer?.name || "Customer",
                  rating: Number(newReview.rating),
                  description: newReview.description
                });


                setShowReviewModal(false);
                setNewReview({ rating: 5, description: "" });

                const updated = await getStoreReviews(tenantId);
                if (updated.success) {
                  setStoreReviews(updated.data.filter(r => r.isActive === 1));
                }

              }}
            >
              إرسال التقييم
            </button>
          </div>
        </div>
      )}
      {/* ===================== MODERN HEADER ===================== */}
      <StoreHeader
        storeData={storeData}
        theme={theme}
        allowThemeChange={allowThemeChange}
        selectedTheme={selectedTheme}
        setSelectedTheme={setSelectedTheme}
        isMobile={isMobile}
        customer={customer}
        showAccountMenu={showAccountMenu}
        setShowAccountMenu={setShowAccountMenu}
        loadCustomerOrders={loadCustomerOrders}
        setShowOrdersModal={setShowOrdersModal}
        handleLogout={handleLogout}
        setShowAuthModal={setShowAuthModal}
        cart={cart}
        setShowCartSidebar={setShowCartSidebar}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />


      {/* ===================== MAIN CONTENT ===================== */}
      <div style={{ minHeight: "calc(100vh - 200px)", padding: isMobile ? "20px 0" : "30px 0" }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto", padding: isMobile ? "0 15px" : "0 20px" }}>
          <div style={{ display: "flex", gap: isMobile ? "20px" : "30px", alignItems: "flex-start", flexWrap: "wrap", flexDirection: isMobile && windowWidth > 992 ? "column" : "row" }}>

            {/* ========== LEFT: CATEGORIES & PRODUCTS ========== */}
            <div style={{ flex: "1", minWidth: isMobile ? "100%" : "300px", width: isMobile ? "100%" : "auto" }}>

              {/* Store Hero Section - Description, Banners, Offers */}
              <HeroSection
                storeData={storeData}
                apiProducts={apiProducts}
                apiCategories={apiCategories}
                apiBanners={apiBanners}
                storeReviews={storeReviews}
                theme={theme}
                isMobile={isMobile}
                isTablet={isTablet}
                policyCardStyle={policyCardStyle}
              />


              {/* Categories - Modern Pills Design */}
              <CategoriesBar
                displayCategories={displayCategories}
                selectedCategory={selectedCategory}
                selectedSubCategory={selectedSubCategory}
                handleCategoryClick={handleCategoryClick}
                isMobile={isMobile}
                isTablet={isTablet}
                theme={theme}
              />


              {/* Products Grid/List */}
              <ProductList
                visibleProducts={visibleProducts}
                theme={theme}
                viewMode={viewMode}
                setViewMode={setViewMode}
                sortValue={sortValue}
                setSortValue={setSortValue}
                isMobile={isMobile}
                isTablet={isTablet}
                handleOpenProduct={handleOpenProduct}
                handleAddToCart={handleAddToCart}
                setProducts={setProducts}
              />



              {/* Customer Reviews Section */}
              {storeReviews.length > 0 && (
                <div style={{
                  width: "100%",
                  background: theme.background,
                  padding: "0 20px 80px",
                  clear: "both"
                }}>
                  <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
                    <h2 style={{ fontWeight: 700, marginBottom: "30px" }}>
                      آراء العملاء
                    </h2>

                    <Swiper
                      style={{ width: "100%" }}
                      spaceBetween={20}
                      slidesPerView={1}
                      breakpoints={{
                        768: { slidesPerView: 2 },
                        1200: { slidesPerView: 3 }
                      }}
                    >
                      {storeReviews.map((r, i) => (
                        <SwiperSlide key={`review-${r.id || r.customerName}-${i}`}>
                          <div style={{
                            background: "white",
                            borderRadius: "14px",
                            padding: "18px",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                            height: "100%"
                          }}>
                            <strong>{r.customerName}</strong>
                            <div style={{ color: "#FFC107", margin: "6px 0" }}>
                              {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                            </div>
                            <p style={{ marginTop: "8px", color: "#555" }}>
                              {r.description}
                            </p>
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                </div>
              )}


            </div>



            {/* ========== RIGHT: CART SIDEBAR ========== */}
            <CartSidebarPage
              tenantId={tenantId}
              theme={theme}
              cities={cities}
              isMobile={isMobile}
              isTablet={isTablet}
              windowWidth={windowWidth}
              cart={cart}
              appliedCoupon={appliedCoupon}
              couponDiscount={couponDiscount}
              subtotal={subtotal}
              shipping={shipping}
              total={total}
              displayCoupons={displayCoupons}
              couponCode={couponCode}
              setCouponCode={setCouponCode}
              couponError={couponError}
              applyCouponCode={applyCouponCode}
              handleApplyCoupon={handleApplyCoupon}
              handleUpdateQty={handleUpdateQty}
              handleRemove={handleRemove}
              deliveryForm={deliveryForm}
              setDeliveryForm={setDeliveryForm}
              handleCheckoutSubmit={handleCheckoutSubmit}
              isSubmitting={isSubmitting}
              setShowAuthModal={setShowAuthModal}
            />
          </div>
        </div>
      </div>

      {/* ===================== FOOTER ===================== */}
      <footer style={{
        background: theme.primaryDark,
        color: "white",
        padding: isMobile ? "20px 0" : "30px 0",
        marginTop: "50px"
      }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto", padding: isMobile ? "0 15px" : "0 20px" }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "20px",
            flexDirection: isMobile ? "column" : "row",
            textAlign: isMobile ? "center" : "right",
          }}>
            <p style={{
              margin: 0,
              fontSize: isMobile ? "12px" : "14px",
              opacity: 0.8,
              order: isMobile ? 2 : (2)
            }}>
              © 2025 {storeData.storeName || storeData.ClientName || "متجرنا"}. {"جميع الحقوق محفوظة."}
            </p>
            <div style={{
              display: "flex",
              gap: "15px",
              flexWrap: "wrap",
              justifyContent: isMobile ? "center" : ("flex-start"),
              order: isMobile ? 1 : (1),
              flexDirection: "row-reverse"
            }}>

              <div className="store-social">
                {storeData.social.facebook && (
                  <a href={storeData.social.facebook} target="_blank" rel="noreferrer" className="social-btn facebook">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                )}
                {storeData.social.instagram && (
                  <a href={storeData.social.instagram} target="_blank" rel="noreferrer" className="social-btn instagram">
                    <i className="fab fa-instagram"></i>
                  </a>
                )}
                {storeData.social.snapchat && (
                  <a href={storeData.social.snapchat} target="_blank" rel="noreferrer" className="social-btn snapchat">
                    <i className="fab fa-snapchat-ghost"></i>
                  </a>
                )}
                {storeData.social.tiktok && (
                  <a href={storeData.social.tiktok} target="_blank" rel="noreferrer" className="social-btn tiktok">
                    <i className="fab fa-tiktok"></i>
                  </a>
                )}
              </div>


            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      {storeData.social.whatsapp && (
        <a
          href={`https://wa.me/${storeData.social.whatsapp.replace(/\D/g, "")}`}
          className="whatsapp-float"
          target="_blank"
          rel="noreferrer"
        >
          <i className="fab fa-whatsapp"></i>
        </a>
      )}


      {/* Product View Modal */}
      <ProductModal
        showProductModal={showProductModal}
        setShowProductModal={setShowProductModal}
        selectedProduct={selectedProduct}
        products={products}
        handleAddToCart={handleAddToCart}
        theme={theme}
        isMobile={isMobile}
      />






      {showOrdersModal && !selectedOrderDetails && (
        <OrdersModal
          show={showOrdersModal}
          onClose={() => setShowOrdersModal(false)}
          orders={customerOrders}
          openOrderDetails={(order) => {
            setSelectedOrderDetails(order);
            setShowOrdersModal(false);
          }}
          theme={theme}
        />

      )}
      {selectedOrderDetails && (
        <OrderDetailsPage
          order={selectedOrderDetails}
          theme={theme}
          onClose={() => setSelectedOrderDetails(null)}
          onEdit={(order) => {
            setEditingOrder(JSON.parse(JSON.stringify(order)));
            setSelectedOrderDetails(null);
          }}
          onDelete={handleDeleteOrder}
        />
      )}



      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          products={apiProducts}
          theme={theme}
          onCancel={() => setEditingOrder(null)}
          onSave={saveEditedOrder}
        />
      )}




      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        tenantId={tenantId}
        theme={{ primary: theme.primary, secondary: theme.primaryDark }}
        onSuccess={(loginResponse) => {
          handleLoginSuccess(loginResponse); // ✅ الآن يتم استدعاؤها فعلياً
          if (pendingCartItem) {
            handleAddToCart(pendingCartItem);
            setPendingCartItem(null);
          }
        }}
      />
    </div>
  );
}
